import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ArrowRight, Check, Printer, Smartphone, Shield, Globe2, Compass, Library } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { RichText } from "@/components/RichText";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const featureIcons = [Library, Compass, BookOpen, Globe2, Shield];
const pTxt = "leading-relaxed text-justify";

export default function Home() {
  const c = useContent();
  const FLIPBOOK_URL = c.flipbook_url;

  const searchParas = [c.home_search_p1, c.home_search_p2, c.home_search_p3];
  const cards = [
    { id: "list-school", title: c.home_card1_title, body: c.home_card1_body, cta: c.home_card1_cta, to: "/list-your-school/listing-options", img: c.home_card1_image },
    { id: "print-edition", title: c.home_card2_title, body: c.home_card2_body, cta: c.home_card2_cta, to: "/contact", img: c.home_card2_image },
    { id: "digital-edition", title: c.home_card3_title, body: c.home_card3_body, cta: c.home_card3_cta, to: "/digital-edition", img: c.home_card3_image },
  ];
  const pills = [c.home_pill_1, c.home_pill_2, c.home_pill_3];
  const features = [1, 2, 3, 4, 5].map((n) => ({ title: c[`home_feature${n}_title`], body: c[`home_feature${n}_body`] }));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#2a0a12]">
        <img
          src={c.hero_bg_image_url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: (Number(c.hero_bg_opacity) || 0) / 100 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a0a12]/85 via-[#2a0a12]/55 to-[#2a0a12]/25" />
        <div className="relative max-w-7xl mx-auto px-6 py-8 lg:py-12 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight whitespace-normal lg:whitespace-nowrap">{c.hero_title}</h1>
            <p className="mt-4 text-lg text-slate-200 max-w-xl leading-relaxed">{c.hero_subtitle}</p>
            <h2 className="mt-4 font-serif text-2xl text-[#C5A059]">{c.hero_headline}</h2>
            <p className="text-slate-200 mt-1 text-lg">{c.hero_tagline}</p>

            <div className="flex flex-wrap gap-3 mt-4">
              {[c.hero_badge_1, c.hero_badge_2].filter(Boolean).map((b) => (
                <span key={b} className="px-4 py-1.5 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-sm">{b}</span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-4">
              <Link to="/digital-edition" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors" data-testid="hero-digital-edition-btn">
                <BookOpen className="h-5 w-5" /> {c.hero_cta}
              </Link>
              <Link to="/digital-edition" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors" data-testid="hero-search-btn">
                {c.hero_secondary_cta} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>

          {/* Cover image -> digital edition page */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="flex justify-center">
            <Link to="/digital-edition" data-testid="hero-flipbook-cover-btn" className="group relative block rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1">
              <img src={c.cover_image_url} alt="BSD Boarding School Directory cover" className="block w-auto max-w-full max-h-[360px] sm:max-h-[460px] lg:max-h-[560px]" />
              <span className="absolute inset-0 flex items-end justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/60 to-transparent">
                <span className="mb-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#800020] font-medium text-sm">
                  <BookOpen className="h-4 w-4" /> Open Digital Edition
                </span>
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SEARCH ON YOUR FINGER TIPS */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-slate-900">{c.home_search_title}</h2>
            <div className="mt-5 space-y-4">
              {searchParas.map((p, i) => (
                <RichText key={i} html={p} className="text-slate-600" />
              ))}
              <RichText html={c.home_search_highlight} className="[&_p]:text-[#800020] [&_p]:font-medium" />
            </div>
            <Link to="/digital-edition" className="mt-6 inline-flex items-center gap-2 text-[#800020] font-medium hover:gap-3 transition-all" data-testid="search-section-cta">
              {c.home_search_cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src={c.home_search_image} alt="Library" className="rounded-xl shadow-xl w-full h-[420px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* PROMO CARDS */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div className="grid md:grid-cols-3 gap-8">
            {cards.map((card, i) => (
              <motion.div key={card.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="rounded-xl border border-slate-200 bg-[#faf9f6] overflow-hidden flex flex-col" data-testid={`promo-card-${card.id}`}>
                <img src={card.img} alt={card.title} className="h-28 w-full object-cover" />
                <div className="p-6 flex flex-col flex-1">
                  <span aria-hidden="true" className="block h-[3px] w-12 rounded-full bg-[#C5A059] mb-4" />
                  <h3 className="font-serif text-2xl lg:text-[1.75rem] font-bold text-[#800020] leading-snug">{card.title}</h3>
                  <RichText html={card.body} className="mt-3 flex-1 [&_p]:text-sm [&_p]:text-slate-600" />
                  <Link to={card.to} className="mt-5 inline-flex items-center gap-2 text-[#800020] font-medium hover:gap-3 transition-all" data-testid={`promo-cta-${card.id}`}>
                    {card.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MULTI DEVICE */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src={c.home_multidevice_image} alt="Students reading" className="rounded-xl shadow-xl w-full h-[420px] object-cover" />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <RichText html={c.home_multidevice_body} className="[&_p]:text-slate-600" />
            <div className="flex flex-wrap gap-3 mt-6">
              {pills.map((p, i) => {
                const Icon = [Library, Printer, Smartphone][i] || Library;
                return (
                  <span key={p} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#faf6f0] border border-[#C5A059]/40 text-sm text-[#800020]">
                    <Icon className="h-4 w-4" /> {p}
                  </span>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* LISTING OPPORTUNITY */}
      <section className="bg-[#2a0a12] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold">{c.home_opportunity_title}</h2>
            <p className={`mt-5 text-slate-300 ${pTxt}`}>{c.home_opportunity_intro}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.slice(0, 3).map((f, i) => {
              const Icon = featureIcons[i] || Check;
              return (
                <motion.div key={f.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="rounded-xl bg-white/5 border border-white/10 p-6" data-testid={`opportunity-feature-${i}`}>
                  <div className="h-11 w-11 rounded-lg bg-[#C5A059]/20 flex items-center justify-center mb-4"><Icon className="h-5 w-5 text-[#C5A059]" /></div>
                  <h3 className="font-serif text-xl mb-2">{f.title}</h3>
                  <p className={`text-sm text-slate-300 ${pTxt}`}>{f.body}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {features.slice(3).map((f, i) => {
              const Icon = featureIcons[i + 3] || Check;
              return (
                <motion.div key={f.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative rounded-xl bg-[#C5A059]/15 border border-[#C5A059]/60 p-7 overflow-hidden" data-testid={`opportunity-feature-${i + 3}`}>
                  <div className="h-12 w-12 rounded-lg bg-[#C5A059] flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-[#2a0a12]" /></div>
                  <h3 className="font-serif text-2xl mb-2 text-[#C5A059]">{f.title}</h3>
                  <p className={`text-sm text-slate-200 ${pTxt}`}>{f.body}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-12">
            <Link to="/list-your-school/listing-options" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C5A059] hover:bg-[#b48f46] text-[#2a0a12] font-semibold transition-colors" data-testid="opportunity-list-cta">
              List Your School <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
